import Enums from './Enums'
import { capitalize } from 'helpers/Utils'
const MILLIS_IN_DAY = 86400000

let formatDate = (locale) => (format) => (date, convertTimezone = true) => 
  date.toLocaleString(locale, { timeZone: (convertTimezone ? undefined : 'UTC'), ...format })

export const getMonthDateYearTimestampString = formatDate('en-us')({ hour12: false, hour: '2-digit', minute: '2-digit', year: 'numeric', month: 'long', day: 'numeric'})
export const getMonthDateYearString = formatDate('en-us')({ year: 'numeric', month: 'long', day: 'numeric'})
export const getMonthString = formatDate('en-us')({ month: 'long'})
export const getYearString = formatDate('en-us')({ year: 'numeric'})
export const getDayString = formatDate('en-us')({ weekday: 'long'})
export const getDayNumber = formatDate('en-us')({ day: '2-digit'})
export const getHourMinuteNumber = formatDate('en-us')({ hour12: false, hour: '2-digit', minute: '2-digit'})
export const getMonthDay = formatDate('en-us')({ month: 'long', day: '2-digit'})
export const getMonthDayYear = formatDate('en-us')({ day: '2-digit', month: '2-digit', year: '2-digit'})
export const getMonthDayFullYear = formatDate('en-us')({ day: '2-digit', month: '2-digit', year: 'numeric'})
export const getMonthDateYearTime = formatDate('en-us')({ hour12: false, hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: '2-digit'})
export const getMonthDateYearTimePeriod = formatDate('en-us')({ hour12: true, hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: '2-digit'})

export const prettifyDuration = (duration) => {
  const second = 1000
  const minute = 60 * second
  const hour = 60 * minute
  const day = 24 * hour

  if(duration > day){
    const numDays = Math.floor(duration / day)
    return numDays + (numDays === 1 ? ' day' : ' days')
  }

  if(duration > hour){
    const numHours = Math.floor(duration / hour)
    return numHours + (numHours === 1 ? ' hour' : ' hours')
  }

  if(duration > minute){
    const numMinutes = Math.floor(duration / minute)
    return numMinutes + (numMinutes === 1 ? ' minute' : ' minutes')
  }

  const numSeconds = Math.floor(duration / second)
  return numSeconds + (numSeconds === 1 ? ' second' : ' seconds')
}

export const getDateString = (date) => {
  let dateString = (new DateFormatter(date)).formatDate()
  return capitalize(dateString)
}
export const getHistoryDateString = (date) => (new HistoryDateFormatter(date)).formatDate()

export const DateEnum = Enums(['year', 'month', 'day', 'today', 'yesterday', 'hour', 'minute', 'second'])

class DateFormatter {
  constructor(date){
    this.date = new Date(Date.parse(date))
  }

  formatDate(){
    let newDate = this.getDate(this.date)

    if (newDate.years > 0) return `${this.formatDateString(newDate.years, DateEnum.year)}`
    if (newDate.months > 0) return `${this.formatDateString(newDate.months, DateEnum.month)}`
    if (newDate.days > 0) return `${this.formatDateString(newDate.days, DateEnum.day)}`
    if (newDate.hours > 0) return `${this.formatDateString(newDate.hours, DateEnum.hour)}`
    if (newDate.minutes > 0) return `${this.formatDateString(newDate.minutes, DateEnum.minute)}`
    if (newDate.seconds > 0) return `${this.formatDateString(newDate.seconds, DateEnum.second)}`
    return `${this.formatDateString(1, DateEnum.second)}`
  }

  formatDateString(number, date){
    let prefix = null
    let suffix = null
    let formattedDate = date

    if (date === DateEnum.day && number === 1){
      formattedDate = DateEnum.yesterday
      prefix = ''
      suffix = ''
    } else {
      prefix = number + ' '
      suffix = ' ago'
    }

    if (this.isPlural(number)) formattedDate += 's'
    return `${prefix}${formattedDate}${suffix}`
  }

  isPlural(number){
    return number > 1
  }

  getDate(before){
    let now = new Date()
    return {
      years: this.yearLength(now),
      months: this.monthLength(now),
      days: this.dayLength(now),
      hours: this.hourLength(now),
      minutes: this.minuteLength(now),
      seconds: this.secondLength(now)
    }
  }

  yearLength(current){
    return current.getFullYear() - this.date.getFullYear()
  }

  monthLength(current, previous){
    return current.getMonth() - this.date.getMonth()
  }

  dayLength(current, previous){
    return current.getDate() - this.date.getDate()
  }

  hourLength(current, previous){
    return current.getHours() - this.date.getHours()
  }

  minuteLength(current, previous){
    return current.getMinutes() - this.date.getMinutes()
  }

  secondLength(current, previous){
    return current.getSeconds() - this.date.getSeconds()
  }
}

class HistoryDateFormatter extends DateFormatter {
  constructor(date){
    super(date)
  }

  isToday(date){
    let isTodayFlag = false

    if (date === DateEnum.hour) isTodayFlag = true
    if (date === DateEnum.minute) isTodayFlag = true
    if (date === DateEnum.second) isTodayFlag = true

    return isTodayFlag
  }

  formatDateString(number, date){
    let prefix = null
    let suffix = null
    let formattedDate = date

    if (this.isToday(date)){
      formattedDate = DateEnum.today
      prefix = ''
      suffix = ''
    } else if (date === DateEnum.day && number === 1){
      formattedDate = DateEnum.yesterday
      prefix = ''
      suffix = ''
    } else {
      prefix = number + ' '
      suffix = ' ago'

      if (this.isPlural(number)) formattedDate += 's'
    }

    return `${prefix}${formattedDate}${suffix}`
  }
}

const addTo = (str) => (len) => (pad) => str.length >= len ? '' : new Array(len - str.length + 1).join(pad)

const addToEnd = (str) => (len) => (pad) => str + addTo(str)(len)(pad)
const addToFront = (str) => (len) => (pad) => addTo(str)(len)(pad) + str

export const addZeroToEnd = (str, len) => addToEnd(str)(len)(0)
export const addZeroToFront = (str, len) => addToFront(str)(len)(0) 

export const convertUTCDateToLocalDate = date => {
  let newDate = new Date(date.getTime()+ date.getTimezoneOffset()*60*1000)

  let offset = date.getTimezoneOffset() / 60
  let hours = date.getHours()

  newDate.setHours(hours - offset)

  return newDate
}

// Weeks start from 1
export const getWeekRange = (week, year) => {
  // Get January 1st of given year
  const janFirst = new Date(year, 0, 1)
  // Get the day of the week (0-6 with 0 being Sunday)
  const dayOfWeek = janFirst.getDay()
  // Convert to Monday-based weekday (0 is now Monday)
  const dayOfWorkWeek = dayOfWeek - 1 > 0 ? dayOfWeek - 1 : 6
  // The first week of the year is the one that contains the first Thursday of January
  // If January 1st lands on weekday that is after Thursday, then the first week of the year is the week after that one
  // Otherwise the first week of the year is the one that contains January 1st
  var firstDayOfFirstWeek
  if(dayOfWeek > 3){
    firstDayOfFirstWeek = janFirst.getTime() + ((7 - dayOfWorkWeek) * MILLIS_IN_DAY)
  } else {
    firstDayOfFirstWeek = janFirst - (dayOfWorkWeek * MILLIS_IN_DAY)
  }
  const firstDayOfGivenWeek = firstDayOfFirstWeek + (7 * (week - 1) * MILLIS_IN_DAY)
  return {first: new Date(firstDayOfGivenWeek), last: new Date(firstDayOfGivenWeek + (6 * MILLIS_IN_DAY))}
}

// If Thursday of 53rd week of year is still in that year, then year has 53 weeks.
export const getNumberOfWorkWeeksInYear = (year) => {
  var lastWeekRange = getWeekRange(53, year)
  if((new Date(lastWeekRange.first.getTime() + 3 * MILLIS_IN_DAY)).getFullYear() === year){
    return 53
  } else {
    return 52
  }
}

export const getCurrentWeekAndYear = (curDate) => {
  // Get January 1st of given year
  const janFirst = new Date(curDate.getFullYear(), 0, 1)
  // Get the day of the week (0-6 with 0 being Sunday)
  const dayOfWeek = janFirst.getDay()
  // Convert to Monday-based weekday (0 is now Monday)
  const dayOfWorkWeek = dayOfWeek - 1 > 0 ? dayOfWeek - 1 : 6
  // The first week of the year is the one that contains the first Thursday of January
  // If January 1st lands on weekday that is after Thursday, then the first week of the year is the week after that one
  // Otherwise the first week of the year is the one that contains January 1st
  var firstDayOfFirstWeek
  if(dayOfWeek > 3){
    firstDayOfFirstWeek = janFirst.getTime() + ((7 - dayOfWorkWeek) * MILLIS_IN_DAY)
  } else {
    firstDayOfFirstWeek = janFirst - (dayOfWorkWeek * MILLIS_IN_DAY)
  }
  const dayDifference = Math.floor((curDate - firstDayOfFirstWeek) / MILLIS_IN_DAY)
  const weekDifference = Math.floor(dayDifference / 7)
  if(weekDifference + 1 === 53 && getNumberOfWorkWeeksInYear(curDate.getFullYear()) != 53){
    return {week: 1, year: curDate.getFullYear() + 1}
  } else {
    return {week: weekDifference + 1, year: curDate.getFullYear()}
  }
}

export const getYearOptions = () => {
  const currentYear = (new Date()).getFullYear()
  return [
    {label: (currentYear - 1), value: (currentYear - 1)}, 
    {label: (currentYear), value: (currentYear)},
    {label: (currentYear + 1), value: (currentYear + 1)}
  ]
}




export const getWeekAndYearBasedonDuration = (requestedLookBack) => {
  const currDate = new Date()

  switch(requestedLookBack) {
    case 'week': {
      const numOfWeeksLookBack = 1
      const dayOfCurrWeek = currDate.getDay()
      const numDayToMonday = ((currDate.getDate()) - dayOfCurrWeek + (dayOfCurrWeek == 0 ? -6:1)) - (numOfWeeksLookBack * 7)
      const oneWeekAgo = currDate.setDate(numDayToMonday + 1) //Use Tuesday to avoid conflict on Monday/Sunday as first day of week
      return getCurrentWeekAndYear(new Date(oneWeekAgo))
    }
    case 'month': {
      const oneMonthAgo = currDate.setMonth(currDate.getMonth() - 1)
      return getCurrentWeekAndYear(new Date(oneMonthAgo))
    }
    case 'year': {
      const oneMonthAgo = currDate.setMonth(currDate.getMonth() - 12)
      return getCurrentWeekAndYear(new Date(oneMonthAgo))
    }
    default: {
      // Fulcrum was launched post January 1, 2018
      return getCurrentWeekAndYear(new Date('January 1, 2018'))
    }
  }

  // const currDate = requestedDate
  // const dayOfCurrWeek = currDate.getDay()
  // const numDayToMonday = ((currDate.getDate()) - dayOfCurrWeek + (dayOfCurrWeek == 0 ? -6:1)) - (numOfWeeksLookBack * 7)
  // const firstDayofWeek = new Date(currDate.setDate(numDayToMonday))
  // const numDayToSunday = firstDayofWeek.getDate() + ((numOfWeeksLookBack * 7) + 6 )
  // const LastDayofWeek = new Date(currDate.setDate(numDayToSunday))
  // return {fromDate: firstDayofWeek, toDate: LastDayofWeek }
}
















